"use client";

import React, { useRef, useState } from "react";
import Map, {
  LngLatBoundsLike,
  MapRef,
  Marker,
  Popup,
} from "react-map-gl/maplibre";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/sharp-solid-svg-icons";
export const mapToken =
  "eyJkcGZ4IjogImNfb2dfYl9zeXN0ZW1lcl93b3JsZCIsICJyZWYiOiAiR3JlZW5sYW5kIiwgInBhciI6ICJjX29nX2Jfc3lzdGVtZXIiLCAicHJpdnMiOiAicmdkamhmVlk3R3RFQTNCWiJ9.UH1hNUReClx6Vp2jtR5z/UJkegSZHH3FFXd7zr7UIz4dJpvKwfI3g74+HUCa0X5590Pt9EfbDlF0MBfD5M6+Hg";
import "maplibre-gl/dist/maplibre-gl.css";

const MapWrapper: React.FC<{
  markers: {
    longitude: number;
    latitude: number;
    zoom?: number;
    popover?: React.ReactNode;
  }[];
}> = ({ markers }) => {
  const [popoverPosition, setPopoverPosition] = useState<"nearPin" | "bottom">(
    "nearPin"
  );
  const [popupInfo, setPopupInfo] = useState<{
    lat: number;
    lng: number;
    popover?: React.ReactNode;
  } | null>(null);
  const mapRef = useRef<MapRef>(null);

  const fitBounds = () => {
    if (!mapRef.current) return;

    const bounds: LngLatBoundsLike = [
      [
        Math.min(...markers.map((m) => m.longitude)),
        Math.min(...markers.map((m) => m.latitude)),
      ],
      [
        Math.max(...markers.map((m) => m.longitude)),
        Math.max(...markers.map((m) => m.latitude)),
      ],
    ];

    mapRef.current.fitBounds(bounds, {
      padding: 120,
      duration: 1000,
      maxZoom: 14,
    });
  };

  return (
    <Map
      initialViewState={
        markers.length === 1
          ? {
              longitude: markers[0].longitude,
              latitude: markers[0].latitude,
              zoom: markers[0].zoom ?? 12,
            }
          : undefined
      }
      cooperativeGestures={true}
      attributionControl={false}
      mapStyle={`https://c_og_b_systemer_world.tiles.viamap.net/v1/style.json?token=${mapToken}`}
      ref={mapRef}
      onLoad={() => {
        fitBounds();
      }}
    >
      {markers.map((marker, index) => (
        <Marker
          key={index}
          longitude={marker.longitude}
          latitude={marker.latitude}
          anchor="bottom"
          onClick={(e) => {
            e.originalEvent.stopPropagation();
            const width = mapRef.current?.getContainer().clientWidth;

            mapRef.current?.flyTo({
              center: [marker.longitude, marker.latitude],
              duration: 1000,
            });

            if (width && width < 768) {
              setPopoverPosition("bottom");
            } else {
              setPopoverPosition("nearPin");
            }

            if (marker.popover) {
              setPopupInfo({
                lat: marker.latitude,
                lng: marker.longitude,
                popover: marker.popover,
              });
            }
          }}
        >
          <FontAwesomeIcon
            className="size-10 text-[var(--map-pin-color)] cursor-pointer"
            icon={faLocationDot}
          />
        </Marker>
      ))}

      <style jsx global>{`
        .maplibregl-popup-content {
          scale: 0.75;
          transform-origin: left;
        }
      `}</style>

      {popupInfo &&
        (popoverPosition === "nearPin" ? (
          <Popup
            latitude={popupInfo.lat}
            longitude={popupInfo.lng}
            anchor="left"
            onClose={() => setPopupInfo(null)}
            offset={[10, -30]}
            maxWidth="none"
          >
            {popupInfo.popover}
          </Popup>
        ) : (
          <div className="absolute bottom-0 right-0 p-2 scale-75 origin-bottom-right">
            <div className="mx-auto max-w-72">{popupInfo.popover}</div>
          </div>
        ))}
    </Map>
  );
};

export default MapWrapper;
